import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamificationApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AgeGroupFilter from '../components/AgeGroupFilter';
import BibleFinisherBadge from '../components/BibleFinisherBadge';
import { INDIVIDUAL_BOARDS, AGE_GROUP_LABELS, isAgeGroupKey } from '../utils/leaderboardConfig';
import './IndividualLeaderboardPage.css';

const CATEGORY_CONFIG = {
  ...Object.fromEntries(
    Object.entries(INDIVIDUAL_BOARDS).map(([key, board]) => [key, {
      title: board.fullTitle,
      description: board.description,
      valueKey: key,
      valueLabel: board.valueLabel,
      showLevel: false
    }])
  ),
  family_chapters: {
    title: '📚 Family chapters — season',
    description: 'Top families ranked by combined total chapters read',
    valueKey: 'chapters',
    valueLabel: 'chapters',
    showLevel: false,
    isFamily: true
  }
};

const IndividualLeaderboardPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { preferences } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe] = useState('all');

  const config = CATEGORY_CONFIG[category];
  const ageParam = searchParams.get('ageGroup') || '';
  const ageGroup = config && !config.isFamily && isAgeGroupKey(ageParam) ? ageParam : '';

  useEffect(() => {
    // Redirect if competitive mode is not enabled
    if (preferences && !preferences.competitiveMode) {
      navigate('/');
      return;
    }

    // Redirect if invalid category
    if (!config) {
      navigate('/leaderboard');
      return;
    }

    if (preferences?.competitiveMode) {
      loadLeaderboard();
    }
  }, [preferences, category, timeframe, ageGroup, config, navigate]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await gamificationApi.getLeaderboard({ 
        limit: 100, // Get full list
        category: category,
        timeframe: timeframe,
        ...(ageGroup ? { ageGroup } : {})
      });

      setLeaderboard(response.data.leaderboard);
      setUnavailable(response.data.available === false);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const handleAgeGroupChange = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set('ageGroup', key);
    else next.delete('ageGroup');
    setSearchParams(next, { replace: true });
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };


  if (!preferences) {
    return (
      <div className="individual-leaderboard-page">
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  if (!preferences.competitiveMode) {
    return <Navigate to="/" replace />;
  }

  if (!config) {
    return (
      <div className="individual-leaderboard-page">
        <div className="leaderboard-error">
          <h2>Invalid category</h2>
          <p>The requested leaderboard category was not found.</p>
          <Link to="/leaderboard" className="back-button">
            Back to leaderboards
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="individual-leaderboard-page">
        <LoadingSpinner size="large" message="Loading leaderboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="individual-leaderboard-page">
        <div className="leaderboard-error">
          <h2>{config.title}</h2>
          <p className="error-message">{error}</p>
          <button onClick={loadLeaderboard} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="individual-leaderboard-page">
      <div className="leaderboard-header">
        <Link to={`/leaderboard${ageGroup ? `?ageGroup=${ageGroup}` : ''}`} className="back-link">
          ← Back to all leaderboards
        </Link>
        <h1>{config.title}</h1>
        <p>{config.description}</p>
        {!config.isFamily && <AgeGroupFilter value={ageGroup} onChange={handleAgeGroupChange} />}
      </div>


      <div className="leaderboard-content">
        <div className="leaderboard-stats">
          <div className="stat-card">
            <div className="stat-number">{leaderboard.length}</div>
            <div className="stat-label">{config.isFamily ? 'Families' : 'Readers'}</div>
          </div>
          {leaderboard.length > 0 && (
            <div className="stat-card">
              <div className="stat-number">
                {formatNumber(leaderboard[0][config.valueKey])}
              </div>
              <div className="stat-label">Top {config.valueLabel}</div>
            </div>
          )}
        </div>

        <div className="leaderboard-list-container">
          {leaderboard.length > 0 ? (
            <div className="leaderboard-list">
              {leaderboard.map((entry) => {
                const entity = config.isFamily ? entry.family : entry.reader;
                const value = entry[config.valueKey];
                
                return (
                  <div key={entity.id} className={`leaderboard-item ${entry.rank <= 3 ? 'top-three' : ''}`}>
                    <div className="rank-section">
                      <div className="rank">{getRankIcon(entry.rank)}</div>
                    </div>
                    
                    <div className="entity-info">
                      <div className="entity-name">
                        {config.isFamily || !entry.hasPublicProfile ? entity.name : (
                          <Link to={`/u/${entity.id}`}>{entity.name}</Link>
                        )}
                        {!config.isFamily && entry.completedBible && <BibleFinisherBadge />}
                      </div>
                      <div className="entity-stats">
                        {config.isFamily && entity.memberCount && (
                          <span className="member-count">{entity.memberCount} members</span>
                        )}
                      </div>
                    </div>

                    <div className="value-display">
                      <div className="value-number">{formatNumber(value)}</div>
                      <div className="value-label">{config.valueLabel}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-leaderboard">
              <div className="empty-icon">🏆</div>
              <h3>{unavailable ? 'Coming soon' : 'No data available'}</h3>
              {unavailable ? (
                <p>We are still counting the words in every chapter. Check back shortly.</p>
              ) : (
              <p>
                {ageGroup
                  ? `No ${AGE_GROUP_LABELS[ageGroup]} readers have logged any ${config.valueLabel} yet.`
                  : `No ${config.isFamily ? 'families' : 'readers'} have logged any ${config.valueLabel} yet.`}
              </p>
              )}
              {!unavailable && <p>Be the first to get on the leaderboard!</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualLeaderboardPage;
